import { emitImpersonationEnded, emitSessionExpired } from './session_events'
import { smartFetch } from './smart_fetch'

// Прозрачное продление сессии на уровне fetch-провайдера openapi-fetch.
// Любой 401 (кроме самих auth-эндпоинтов) → один POST /auth/refresh →
// повтор исходного запроса. Если refresh не помог — сигнал session-expired
// (стор очистит юзера, роутер уведёт на /login без белого экрана).
//
// Refresh — single-flight: бэк ротирует refresh-токен, поэтому два
// параллельных refresh инвалидировали бы друг друга. Все одновременные 401
// ждут один и тот же промис.

// POST /auth/refresh, POST/DELETE /sessions (login/logout) и сам старт/стоп
// имперсонации (POST/DELETE /impersonations) 401-ят штатно — их не заворачиваем
// в refresh-retry, иначе цикл.
const AUTH_ENDPOINT = /\/api\/v1\/(auth\/refresh|sessions|impersonations)(\?|$|\/)/

let refreshInFlight: Promise<boolean> | null = null

// Имперсонация оборвалась (E18, §11): запрос под имперсонацией вернул 401 с
// кодом `impersonation_ended` (цель деактивирована). После успешного refresh
// (бэк закрыл имперсонацию, вернул админа) — синхронизируем стор + тост.
async function isImpersonationEnded(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as { error?: string }
    return body.error === 'impersonation_ended'
  } catch {
    return false
  }
}

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

// Refresh идёт голым fetch (в обход smartFetch — иначе рекурсия через
// fetchWithRefresh), поэтому встроенных TTFB/total-таймаутов у него нет. При
// тихом дропе пакетов запрос висел бы бесконечно, а все 401-запросы ждут этот
// единый промис (single-flight) → приложение замирает. Ограничиваем 5 секундами
// (M-fe11): таймаут → AbortError → false → session-expired, юзер уходит на /login,
// а не в вечную загрузку.
const REFRESH_TIMEOUT_MS = 5000

async function doRefresh(): Promise<boolean> {
  try {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    })
    return response.ok
  } catch {
    return false
  }
}

export async function fetchWithRefresh(request: Request): Promise<Response> {
  // Клон снимаем ДО первой отправки — после тело запроса уже вычитано.
  const retryClone = AUTH_ENDPOINT.test(request.url) ? null : request.clone()

  const response = await smartFetch(request)
  if (response.status !== 401 || retryClone === null) return response

  const ended = await isImpersonationEnded(response)
  const refreshed = await refreshSession()
  if (!refreshed) {
    emitSessionExpired()
    return response
  }

  const retried = await smartFetch(retryClone)
  if (retried.status === 401) {
    emitSessionExpired()
    return retried
  }

  // Refresh вернул админа после обрыва имперсонации — уведомляем приложение
  // (стор вернётся к админу, баннер исчезнет, тост). Делаем после успешного
  // повтора, чтобы данные страницы уже соответствовали админу.
  if (ended) emitImpersonationEnded()
  return retried
}
